/**
 * 🐦📓 Cassandra's Ledger Insights — personalized from YOUR receipts.
 *
 * Rendered inside the Kitchen Hub's Budget POV so Cassandra can talk
 * about the user's actual grocery spending — not a generic recipe cost.
 *
 * Fed by GET /api/cassandra-ledger/insights which returns:
 *   { total_receipts, total_spend,
 *     frequent_items[]  {name, category, buy_count, total_spend, avg_unit_price},
 *     category_spend[]  {category, total_spend},
 *     substitution_hints[] {name, cheapest_price, priciest_price,
 *                          potential_savings_per_unit, times_bought} }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  produce: "🥦", protein: "🥩", dairy: "🥛", grain: "🌾",
  pantry: "🥫", snack: "🍿", beverage: "🥤", household: "🧴", other: "🛒",
};

function money(v, currency = "USD") {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return "$0.00";
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n); }
  catch { return `$${n.toFixed(2)}`; }
}

export default function CassandraInsightsPanel() {
  const [ins, setIns] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cassandra-ledger/insights?top_n=8`)
      .then((r) => setIns(r.data))
      .catch(() => setErr("Insights unavailable — try again in a moment."));
  }, []);

  if (err) return null;
  if (!ins) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-black/40 p-4 text-xs text-neutral-500"
           data-testid="cassandra-insights-loading">
        Cassandra is flipping through your receipts…
      </div>
    );
  }

  const empty = (ins.total_receipts || 0) === 0;
  if (empty) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-black p-5"
           data-testid="cassandra-insights-empty">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">
          🐦📓 Cassandra&apos;s Personal Ledger
        </div>
        <div className="text-sm text-neutral-300 italic mb-3">
          I&apos;d love to audit your real receipts, but you haven&apos;t handed me any yet.
          Snap one from the Kitchen or Gym and I&apos;ll turn it into insights.
        </div>
        <Link
          to="/kitchen"
          className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 inline-block"
          data-testid="cassandra-insights-cta"
        >
          📷 Scan a receipt
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-black p-5 space-y-4"
         data-testid="cassandra-insights-panel">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">
          🐦📓 Cassandra&apos;s Personal Ledger · {ins.total_receipts} receipt{ins.total_receipts === 1 ? "" : "s"} audited
        </div>
        <div className="text-sm text-neutral-200 italic leading-relaxed">
          You&apos;ve spent <b className="text-emerald-300">{money(ins.total_spend)}</b> across your scanned
          grocery runs. Here&apos;s where your money goes — and where I&apos;d shave it.
        </div>
      </div>

      {/* Category spend */}
      {ins.category_spend?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
            💰 Spend by Category
          </div>
          <div className="space-y-1.5">
            {ins.category_spend.slice(0, 6).map((row) => {
              const pct = ins.total_spend > 0
                ? Math.round((row.total_spend / ins.total_spend) * 100)
                : 0;
              return (
                <div key={row.category} className="flex items-center gap-2 text-xs"
                     data-testid={`insight-cat-${row.category}`}>
                  <span className="w-6 text-center">{CATEGORY_ICONS[row.category] || "🛒"}</span>
                  <span className="w-20 capitalize text-neutral-300">{row.category}</span>
                  <div className="flex-1 h-2 rounded-full bg-black/60 border border-neutral-800 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-500 to-emerald-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-emerald-300 font-mono min-w-[60px] text-right">
                    {money(row.total_spend)}
                  </span>
                  <span className="text-neutral-500 min-w-[36px] text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Frequent items */}
      {ins.frequent_items?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
            🛒 Your Kitchen Staples
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {ins.frequent_items.slice(0, 6).map((it, i) => (
              <div key={i}
                   className="flex items-center gap-2 text-xs rounded-lg bg-black/40 border border-neutral-800 px-2 py-1.5"
                   data-testid={`insight-item-${i}`}>
                <span className="w-5 text-center">{CATEGORY_ICONS[it.category] || "🛒"}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-neutral-200 truncate">{it.name}</div>
                  <div className="text-[10px] text-neutral-500">
                    bought {it.buy_count}× · avg {money(it.avg_unit_price)}
                  </div>
                </div>
                <div className="text-emerald-300 font-mono">{money(it.total_spend)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Substitution hints — the real audit */}
      {ins.substitution_hints?.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-black p-3">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">
            🐦 Ledger&apos;s Audit Tip · Cheaper Swaps from Your Own History
          </div>
          <div className="text-[11px] text-amber-100/80 italic mb-2">
            Same item, different price on different visits. Next time, aim for the low.
          </div>
          <div className="space-y-1.5">
            {ins.substitution_hints.slice(0, 5).map((h, i) => (
              <div key={i}
                   className="flex items-center gap-2 text-xs rounded bg-black/40 border border-neutral-900 px-2 py-1"
                   data-testid={`insight-sub-${i}`}>
                <span className="w-5 text-center">{CATEGORY_ICONS[h.category] || "🛒"}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-amber-100 truncate">{h.name}</div>
                  <div className="text-[10px] text-neutral-500">
                    {money(h.cheapest_price)} – {money(h.priciest_price)} · seen {h.times_bought}×
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-emerald-300 font-mono font-bold">
                    -{money(h.potential_savings_per_unit)}
                  </div>
                  <div className="text-[9px] text-neutral-500">per unit</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-right">
        <Link
          to="/receipts"
          className="text-[11px] text-emerald-300 hover:text-emerald-200 underline underline-offset-2"
          data-testid="cassandra-insights-view-all"
        >
          Open full ledger →
        </Link>
      </div>
    </div>
  );
}
